import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowRight, Banknote, Check, ChevronDown, ChevronUp, Users } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useCurrency } from '@/contexts/CurrencyContext'
import apiClient, { ApiClientError } from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import type { BusinessDay, CashDrawerMovement, ShiftClosing } from '@/types'
import { NumericKeypad } from './NumericKeypad'
import { TenderCountStep } from './TenderCountStep'

/** Fallback in PKR. Mirrors backend dayops.DefaultVarianceThreshold. */
const DEFAULT_VARIANCE_THRESHOLD = 100

interface ShiftCloseWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  day: BusinessDay
  /** Whole-day tender totals so far (from getCurrentDayStatus). */
  cashSales: number
  cardSales: number
  onlineSales: number
  /** Shift closes already recorded today (used to window this shift). */
  priorShifts: ShiftClosing[]
  /** Real open orders — shown as a non-blocking FYI (dinner spans shifts). */
  unresolvedOpenOrders: number
  movements: CashDrawerMovement[] | undefined
  isLoadingSummary: boolean
}

type Step = 'count-cash' | 'count-card' | 'count-online' | 'cash-drop' | 'review'

const STEP_ORDER: Step[] = ['count-cash', 'count-card', 'count-online', 'cash-drop', 'review']

/**
 * Shift close wizard (F-CTRL-N29) — a mid-day drawer reconciliation at
 * handover. The X-report sibling of CloseDayWizard: same plain-language,
 * cashier-first tone, but the day stays OPEN and there is no stage/lock —
 * a single shiftCloseDay call records the snapshot.
 *
 *   Step 1: count the cash in the drawer
 *   Step 2: card machine total
 *   Step 3: online & wallet total
 *   Step 4: move cash to the safe? (optional, continuous drawer)
 *   Step 5: review and finish
 *
 * The shift's expected cash is derived from the whole-day totals minus the
 * prior shifts' windows — the same arithmetic the backend seals authoritatively.
 *
 * F-CTRL-N33 split the old single, skippable, keypad-less "card and online"
 * screen into two mandatory keypad steps, matching the day close. The handover
 * carries the same risk: a shift that hands over an uncounted card batch leaves
 * a gap that surfaces (if ever) at the day close, by which point it cannot be
 * attributed to a shift or a person. Only the cash drop stays optional — that
 * is a decision about where to put money, not a count of money taken.
 */
export function ShiftCloseWizard({
  open,
  onOpenChange,
  day,
  cashSales,
  cardSales,
  onlineSales,
  priorShifts,
  unresolvedOpenOrders,
  movements,
  isLoadingSummary,
}: ShiftCloseWizardProps) {
  const { formatCurrency } = useCurrency()
  const qc = useQueryClient()

  const shiftNumber = priorShifts.length + 1
  const prior = priorShifts.length > 0 ? priorShifts[priorShifts.length - 1] : null

  // Continuous drawer: this shift opened on the prior shift's counted cash
  // minus its drop; shift 1 opens on the day's float.
  const shiftOpening = prior ? prior.counted_cash - prior.cash_drop : day.opening_cash ?? 0
  const windowStart = prior ? prior.closed_at : day.opened_at

  // This shift's window figures = whole-day totals minus what prior shifts
  // already reconciled (their windows), mirroring the backend split.
  const priorCash = priorShifts.reduce((s, sh) => s + sh.shift_cash_sales, 0)
  const priorCard = priorShifts.reduce((s, sh) => s + sh.shift_card_sales, 0)
  const priorOnline = priorShifts.reduce((s, sh) => s + sh.shift_online_sales, 0)
  const shiftCashSales = Math.max(0, cashSales - priorCash)
  const shiftCardSales = Math.max(0, cardSales - priorCard)
  const shiftOnlineSales = Math.max(0, onlineSales - priorOnline)

  // Net adjustments inside THIS shift's window, excluding handover drops
  // (those live in the next shift's opening) — matches shiftWindowedNetAdj.
  const shiftNetAdj = useMemo(() => {
    const start = new Date(windowStart).getTime()
    return (movements ?? [])
      .filter((m) => new Date(m.created_at).getTime() >= start)
      .filter((m) => !(m.movement_type === 'paid_out' && (m.notes ?? '').includes('shift_handover=')))
      .reduce((acc, m) => (m.movement_type === 'paid_in' ? acc + m.amount : acc - m.amount), 0)
  }, [movements, windowStart])

  const expectedCash = shiftOpening + shiftCashSales + shiftNetAdj

  const [step, setStep] = useState<Step>('count-cash')
  const [countedCash, setCountedCash] = useState('')
  const [countedCard, setCountedCard] = useState('')
  const [countedOnline, setCountedOnline] = useState('')
  const [cashDrop, setCashDrop] = useState('')
  const [notes, setNotes] = useState('')
  const [showMath, setShowMath] = useState(false)

  useEffect(() => {
    if (open) {
      setStep('count-cash')
      setCountedCash('')
      setCountedCard('')
      setCountedOnline('')
      setCashDrop('')
      setNotes('')
      setShowMath(false)
    }
  }, [open])

  const parsedCash = Number(countedCash) || 0
  const parsedCard = Number(countedCard) || 0
  const parsedOnline = Number(countedOnline) || 0
  const parsedDrop = Number(cashDrop) || 0
  const cashVariance = parsedCash - expectedCash
  const cardEntered = countedCard.trim() !== '' && Number.isFinite(Number(countedCard))
  const onlineEntered = countedOnline.trim() !== '' && Number.isFinite(Number(countedOnline))
  const isCountValid = countedCash.trim() !== '' && Number.isFinite(Number(countedCash))
  const dropTooBig = parsedDrop > parsedCash + 0.001
  const carriedForward = Math.max(0, parsedCash - parsedDrop)

  const thresholdQuery = useQuery({
    queryKey: ['settings', 'day_close_variance_threshold'],
    queryFn: () => apiClient.getSetting('day_close_variance_threshold'),
    staleTime: 5 * 60_000,
  })
  const varianceThreshold = useMemo(() => {
    const raw = thresholdQuery.data?.data
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) && n >= 0 ? n : DEFAULT_VARIANCE_THRESHOLD
  }, [thresholdQuery.data])

  const absVariance = Math.abs(cashVariance)
  const isMatch = absVariance < 0.01
  const isSmallOff = !isMatch && absVariance <= varianceThreshold
  const isBigOff = absVariance > varianceThreshold

  const cardShort = cardEntered ? Math.max(0, shiftCardSales - parsedCard) : 0
  const cardOver = cardEntered ? Math.max(0, parsedCard - shiftCardSales) : 0
  const onlineShort = onlineEntered ? Math.max(0, shiftOnlineSales - parsedOnline) : 0
  const onlineOver = onlineEntered ? Math.max(0, parsedOnline - shiftOnlineSales) : 0
  const cardUnexplained = cardShort + cardOver
  const onlineUnexplained = onlineShort + onlineOver

  // Variance note gate (accountability), mirroring the day close — but NO
  // open-orders gate: dinner service spans shifts, so open tabs are expected
  // mid-shift and are shown as information only.
  const varianceNeedsNote =
    isBigOff || cardUnexplained > varianceThreshold || onlineUnexplained > varianceThreshold
  const noteRequired = varianceNeedsNote && notes.trim().length === 0

  // Every tender, for the headline and the breakdown. The banner used to report
  // cash alone, so a handover could read "Rs 500 short" while the card batch was
  // thousands out. Not summed: separate pots, and netting would hide one fault
  // behind another. See CloseDayWizard's SummaryBanner for the full reasoning.
  const shiftTenders = [
    {
      label: 'Cash',
      over: cashVariance > 0 ? cashVariance : 0,
      short: cashVariance < 0 ? -cashVariance : 0,
      needsNote: absVariance > varianceThreshold,
    },
    { label: 'Card', over: cardOver, short: cardShort, needsNote: cardUnexplained > varianceThreshold },
    {
      label: 'Online',
      over: onlineOver,
      short: onlineShort,
      needsNote: onlineUnexplained > varianceThreshold,
    },
  ]
  const shiftOffTenders = shiftTenders.filter((t) => t.over > 0.005 || t.short > 0.005)

  const shiftMut = useMutation({
    mutationFn: () =>
      apiClient.shiftCloseDay(day.id, {
        counted_cash: parsedCash,
        // Always sent (F-CTRL-N33) — the steps can't be advanced without a
        // number, so a 0 here means "took nothing", never "not counted".
        counted_card: parsedCard,
        counted_online: parsedOnline,
        cash_drop: parsedDrop > 0 ? parsedDrop : undefined,
        notes: notes.trim() || undefined,
      }),
  })

  const finish = async () => {
    try {
      const res = await shiftMut.mutateAsync()
      if (!res.success) {
        toastHelpers.error(res.message || 'Failed to close shift')
        return
      }
      toastHelpers.success(`Shift ${shiftNumber} closed`)
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['shift-closings'] })
      qc.invalidateQueries({ queryKey: ['currentDayStatus'] })
      onOpenChange(false)
    } catch (err) {
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      // Unreachable in normal use — kept for a stale tab on an older bundle.
      if (err instanceof ApiClientError && err.code === 'tender_count_required') {
        setStep((err.missingTenders ?? []).includes('online') ? 'count-online' : 'count-card')
        toastHelpers.error(
          'Count this first',
          'Enter the total for every payment type — type 0 if it took nothing.',
        )
        return
      }
      toastHelpers.apiError('Close shift', err as Error)
    }
  }

  const finishing = shiftMut.isPending

  // Linear navigation; every counting step is mandatory (F-CTRL-N33). The cash
  // drop stays optional — blank means "carry the whole drawer forward".
  const stepIndex = STEP_ORDER.indexOf(step)
  const goNext = () => setStep(STEP_ORDER[Math.min(stepIndex + 1, STEP_ORDER.length - 1)])
  const goBack = () => setStep(STEP_ORDER[Math.max(stepIndex - 1, 0)])
  const canAdvance =
    step === 'count-cash'
      ? isCountValid
      : step === 'count-card'
        ? cardEntered
        : step === 'count-online'
          ? onlineEntered
          : step === 'cash-drop'
            ? !dropTooBig
            : true

  return (
    <Dialog open={open} onOpenChange={(o) => !finishing && onOpenChange(o)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {step === 'count-cash' && `Close shift ${shiftNumber} — count the drawer`}
            {step === 'count-card' && 'Card machine total'}
            {step === 'count-online' && 'Online & wallet total'}
            {step === 'cash-drop' && 'Move cash to the safe?'}
            {step === 'review' && "Here's how the shift went"}
          </DialogTitle>
          <DialogDescription className="text-base">
            {step === 'count-cash' &&
              'Handover time. Count every note and coin in the till and type the total.'}
            {step === 'count-card' &&
              "This shift's total on the card machine. If it took nothing, enter 0."}
            {step === 'count-online' &&
              "This shift's online and wallet total. If there were none, enter 0."}
            {step === 'cash-drop' &&
              'Leaving a big pile of cash in the drawer is risky. Move some to the safe now — the next shift starts fresh. Optional.'}
            {step === 'review' && 'Check the numbers, then finish the shift. The day stays open.'}
          </DialogDescription>
          <ShiftStepIndicator step={step} />
        </DialogHeader>

        {step === 'count-cash' && (
          <TenderCountStep
            id="shift-counted-cash"
            label="Money you counted in the drawer"
            value={countedCash}
            onChange={setCountedCash}
          />
        )}

        {step === 'count-card' && (
          <TenderCountStep
            id="shift-counted-card"
            label="Total on the card machine this shift"
            value={countedCard}
            onChange={setCountedCard}
            expectedLabel={`This shift's card sales: ${formatCurrency(shiftCardSales)}`}
            reconciliation={
              cardEntered
                ? {
                    matches: cardUnexplained < 0.5,
                    text:
                      cardUnexplained < 0.5
                        ? '✓ Reconciles'
                        : cardOver > 0
                          ? `Over by ${formatCurrency(cardOver)}`
                          : `Short by ${formatCurrency(cardShort)}`,
                  }
                : undefined
            }
          />
        )}

        {step === 'count-online' && (
          <TenderCountStep
            id="shift-counted-online"
            label="Online &amp; wallet total this shift"
            value={countedOnline}
            onChange={setCountedOnline}
            expectedLabel={`This shift's online sales: ${formatCurrency(shiftOnlineSales)}`}
            reconciliation={
              onlineEntered
                ? {
                    matches: onlineUnexplained < 0.5,
                    text:
                      onlineUnexplained < 0.5
                        ? '✓ Reconciles'
                        : onlineOver > 0
                          ? `Over by ${formatCurrency(onlineOver)}`
                          : `Short by ${formatCurrency(onlineShort)}`,
                  }
                : undefined
            }
          />
        )}

        {step === 'cash-drop' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border-2 border-sky-200 bg-sky-50 p-4 dark:border-sky-900 dark:bg-sky-950/40">
              <div className="rounded-full bg-sky-500 p-1.5 shrink-0">
                <Banknote className="h-5 w-5 text-white" />
              </div>
              <div className="text-sm text-sky-900 dark:text-sky-100">
                You counted <span className="font-semibold">{formatCurrency(parsedCash)}</span>. Move
                some of it to the safe and the next shift opens on a smaller, safer float. Leave this
                blank to carry the whole drawer forward.
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="shift-cash-drop" className="text-base">
                Amount to move to the safe (optional)
              </Label>
              <Input
                id="shift-cash-drop"
                type="text"
                inputMode="decimal"
                value={cashDrop}
                onChange={(e) => setCashDrop(e.target.value.replace(/[^0-9.]/g, ''))}
                className={cn(
                  'h-14 text-2xl font-semibold tabular-nums',
                  dropTooBig && 'border-rose-400 focus-visible:ring-rose-400',
                )}
              />
              {dropTooBig ? (
                <p className="text-sm font-medium text-rose-600">
                  You can't move more than you counted ({formatCurrency(parsedCash)}).
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Next shift will start with{' '}
                  <span className="font-medium text-foreground">{formatCurrency(carriedForward)}</span>
                </p>
              )}
            </div>
            <NumericKeypad value={cashDrop} onChange={setCashDrop} />
          </div>
        )}

        {step === 'review' && (
          <div className="space-y-4">
            {isLoadingSummary ? (
              <div className="h-20 animate-pulse rounded-xl border-2 border-muted bg-muted/30" />
            ) : (
              <ShiftBanner
                offTenders={shiftOffTenders}
                needsNote={varianceNeedsNote}
                formatCurrency={formatCurrency}
              />
            )}

            <button
              type="button"
              onClick={() => setShowMath((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg border bg-muted/30 px-4 py-3 text-left hover:bg-muted/50"
            >
              <span className="text-sm font-medium">Show how we got these numbers</span>
              {showMath ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {/* F-CTRL-N33: all three tenders side by side. Every one has been
                counted by this point, so the handover summary is complete
                rather than cash-only. */}
            <div className="space-y-1.5 rounded-lg border bg-card px-4 py-3">
              <div className="text-sm font-semibold text-muted-foreground">By payment type</div>
              {shiftTenders.map((t) => (
                <ShiftTenderRow
                  key={t.label}
                  label={t.label}
                  short={t.short}
                  over={t.over}
                  needsNote={t.needsNote}
                  formatCurrency={formatCurrency}
                />
              ))}
            </div>
            {showMath && (
              <div className="space-y-1.5 rounded-lg border bg-card px-4 py-3 text-base">
                <MathRow label="Shift started with" value={formatCurrency(shiftOpening)} />
                <MathRow label="+ Cash this shift" value={formatCurrency(shiftCashSales)} />
                {shiftNetAdj !== 0 && (
                  <MathRow label="± Money in/out" value={formatCurrency(shiftNetAdj)} />
                )}
                <div className="my-1 border-t" />
                <MathRow label="Should be in drawer" value={formatCurrency(expectedCash)} bold />
                <MathRow label="You counted" value={formatCurrency(parsedCash)} bold />
                <div className="my-1 border-t" />
                <MathRow
                  label="Difference"
                  value={`${formatCurrency(Math.abs(cashVariance))} ${isMatch ? '' : cashVariance < 0 ? 'short' : 'over'}`.trim()}
                  bold
                  className={cn(
                    isMatch && 'text-emerald-700 dark:text-emerald-300',
                    isSmallOff && 'text-amber-700 dark:text-amber-300',
                    isBigOff && 'text-rose-700 dark:text-rose-300',
                  )}
                />
                {parsedDrop > 0 && (
                  <>
                    <div className="my-1 border-t" />
                    <MathRow label="Moved to safe" value={formatCurrency(parsedDrop)} />
                    <MathRow label="Carried to next shift" value={formatCurrency(carriedForward)} bold />
                  </>
                )}
              </div>
            )}

            {unresolvedOpenOrders > 0 && (
              <div className="flex items-start gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {unresolvedOpenOrders} table{unresolvedOpenOrders === 1 ? '' : 's'} still open
                  </span>{' '}
                  — that's fine mid-shift. They carry over to the next shift and settle at the day close.
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="shift-notes" className="text-base">
                Note for the owner
                {varianceNeedsNote ? (
                  <span className="ml-1 text-sm font-normal text-rose-700 dark:text-rose-300">
                    (required — please explain the difference)
                  </span>
                ) : (
                  <span className="ml-1 text-sm font-normal text-muted-foreground">(optional)</span>
                )}
              </Label>
              <Textarea
                id="shift-notes"
                rows={2}
                placeholder="e.g. drawer short — gave change from own pocket, will settle"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={cn(noteRequired && 'border-rose-400 focus-visible:ring-rose-400')}
              />
            </div>

            <p className="text-sm text-muted-foreground">
              This records the shift handover. The day stays open and sales keep counting.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {/* No Skip on any counting step (F-CTRL-N33). */}
          {step !== 'review' && (
            <>
              <Button
                variant="outline"
                size="lg"
                className="h-12"
                onClick={step === 'count-cash' ? () => onOpenChange(false) : goBack}
              >
                {step === 'count-cash' ? 'Cancel' : 'Back'}
              </Button>
              <Button
                size="lg"
                className="h-12 flex-1 sm:flex-none"
                onClick={goNext}
                disabled={!canAdvance}
              >
                Next <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </>
          )}
          {step === 'review' && (
            <>
              <Button
                variant="outline"
                size="lg"
                className="h-12"
                onClick={goBack}
                disabled={finishing}
              >
                Back
              </Button>
              <Button
                size="lg"
                className="h-12 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={finish}
                disabled={finishing || noteRequired || isLoadingSummary}
              >
                {finishing ? 'Saving…' : `Finish shift ${shiftNumber}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ShiftStepIndicator({ step }: { step: Step }) {
  const n = STEP_ORDER.indexOf(step) + 1
  const total = STEP_ORDER.length
  return (
    <div className="flex items-center gap-1.5 pt-2" aria-label={`Step ${n} of ${total}`}>
      {STEP_ORDER.map((_, i) => (
        <span
          key={i}
          className={cn(
            'h-2 rounded-full transition-all',
            i + 1 === n ? 'w-8 bg-foreground' : 'w-2 bg-muted',
            i + 1 < n && 'bg-emerald-500',
          )}
          aria-hidden="true"
        />
      ))}
      <span className="ml-2 text-sm text-muted-foreground">
        Step {n} of {total}
      </span>
    </div>
  )
}

/** Handover headline across every tender. Mirrors CloseDayWizard's SummaryBanner. */
function ShiftBanner({
  offTenders,
  needsNote,
  formatCurrency,
}: {
  offTenders: Array<{ label: string; over: number; short: number }>
  needsNote: boolean
  formatCurrency: (n: number) => string
}) {
  if (offTenders.length === 0) {
    return (
      <div className="flex items-start gap-3 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/40">
        <div className="rounded-full bg-emerald-500 p-1.5 shrink-0">
          <Check className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="text-lg font-bold text-emerald-900 dark:text-emerald-100">
            Everything matches!
          </div>
          <div className="text-sm text-emerald-800 dark:text-emerald-200">
            This shift's cash, card and online all match. Great job.
          </div>
        </div>
      </div>
    )
  }
  const tone = needsNote
    ? { border: 'border-rose-300 dark:border-rose-800', bg: 'bg-rose-50 dark:bg-rose-950/40', chip: 'bg-rose-500', title: 'text-rose-900 dark:text-rose-100', body: 'text-rose-800 dark:text-rose-200' }
    : { border: 'border-amber-300 dark:border-amber-800', bg: 'bg-amber-50 dark:bg-amber-950/40', chip: 'bg-amber-500', title: 'text-amber-900 dark:text-amber-100', body: 'text-amber-800 dark:text-amber-200' }

  const single = offTenders.length === 1 ? offTenders[0] : null
  const headline = single
    ? `${single.label} ${formatCurrency(single.over > 0 ? single.over : single.short)} ${single.over > 0 ? 'over' : 'short'}`
    : `${offTenders.length} payment types don't match`
  const detail = needsNote
    ? `${offTenders.map((t) => t.label).join(', ')} — please write a note below.`
    : `${offTenders.map((t) => t.label).join(', ')} slightly off. Add a note if you know why.`

  return (
    <div className={cn('flex items-start gap-3 rounded-xl border-2 p-4', tone.border, tone.bg)}>
      <div className={cn('rounded-full p-1.5 shrink-0', tone.chip)}>
        <AlertTriangle className="h-5 w-5 text-white" />
      </div>
      <div>
        <div className={cn('text-lg font-bold', tone.title)}>{headline}</div>
        <div className={cn('text-sm', tone.body)}>{detail}</div>
      </div>
    </div>
  )
}

/** One tender's short/over on the shift review. Mirrors the day close's TenderBreakdown row. */
function ShiftTenderRow({
  label,
  short,
  over,
  needsNote,
  formatCurrency,
}: {
  label: string
  short: number
  over: number
  needsNote: boolean
  formatCurrency: (n: number) => string
}) {
  const isOver = over > 0.5
  const isShort = short > 0.5
  const matches = !isOver && !isShort
  return (
    <div className="flex items-center justify-between text-base">
      <span>{label}</span>
      <span
        className={cn(
          'tabular-nums font-medium',
          matches && 'text-emerald-600 dark:text-emerald-300',
          !matches && needsNote && 'text-rose-600 dark:text-rose-300',
          !matches && !needsNote && 'text-amber-600 dark:text-amber-300',
        )}
      >
        {matches
          ? '✓ Matches'
          : isOver
            ? `${formatCurrency(over)} over`
            : `${formatCurrency(short)} short`}
      </span>
    </div>
  )
}

function MathRow({
  label,
  value,
  bold,
  className,
}: {
  label: string
  value: string
  bold?: boolean
  className?: string
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={cn(bold && 'font-semibold')}>{label}</span>
      <span className={cn('tabular-nums', bold ? 'font-bold' : 'font-medium', className)}>{value}</span>
    </div>
  )
}
